import type { OnboardingRecommendationAnswers } from "@repo/types";
import { Button } from "~/components/ui/button";
import { PortfolioHeroCard } from "~/components/ui/portfolio-hero-card";
import { getOnboardingV1RecommendationReturnDisplay } from "~/features/start/data/onboarding-v1-recommendation-return";
import {
  useOnboardingV1Recommendations,
  type OnboardingV1RecommendationCard,
  type OnboardingV1RecommendationDataSource,
} from "~/features/start/state/use-onboarding-v1-recommendations";
import { formatSignedPercent } from "~/lib/display-format";
import { getPublicAssetUrl } from "~/lib/env";
import { m as messages } from "~/lib/i18n";
import { OnboardingV1RetryState } from "./onboarding-v1-retry-state";

interface OnboardingV1PickStepProps {
  readonly answers: OnboardingRecommendationAnswers;
  readonly dataSource?: OnboardingV1RecommendationDataSource;
  readonly onContinue: () => void;
  readonly onExplore: () => void;
  readonly onSelect: (blueprintId: string) => void;
  readonly selectedBlueprintId: string | null;
}

function RecommendationCard(props: {
  readonly card: OnboardingV1RecommendationCard;
  readonly onSelect: () => void;
  readonly selected: boolean;
}) {
  const title =
    props.card.blueprintName ??
    messages.onboarding_v1_pick_untitled_portfolio();
  const returnDisplay = getOnboardingV1RecommendationReturnDisplay(props.card);
  const returnLabel =
    returnDisplay.period === "one_year"
      ? messages.onboarding_v1_pick_return_label()
      : returnDisplay.period === "all_time"
        ? messages.portfolio_hero_all_time_return_label()
        : messages.onboarding_flow_not_available_label();

  return (
    <PortfolioHeroCard
      accessibleLabel={messages.onboarding_v1_pick_select_portfolio({ title })}
      className="onboarding-v1__portfolio-card"
      imageSrc={getPublicAssetUrl(props.card.heroImagePath)}
      onSelect={props.onSelect}
      performanceLabel={returnLabel}
      performanceTone={
        returnDisplay.returnPct === null
          ? "neutral"
          : returnDisplay.returnPct < 0
            ? "negative"
            : "positive"
      }
      performanceValue={
        returnDisplay.returnPct === null
          ? "—"
          : formatSignedPercent(returnDisplay.returnPct, {
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            })
      }
      selected={props.selected}
      title={title}
      tokens={props.card.tokens}
    />
  );
}

function PickLoadingState() {
  return (
    <div
      aria-label={messages.onboarding_v1_pick_loading()}
      aria-live="polite"
      className="onboarding-v1__pick-loading"
      role="status"
    >
      {[0, 1, 2].map((index) => (
        <span
          aria-hidden="true"
          className="onboarding-v1__portfolio-card-skeleton"
          key={index}
        />
      ))}
    </div>
  );
}

export function OnboardingV1PickStep(props: OnboardingV1PickStepProps) {
  const recommendations = useOnboardingV1Recommendations({
    answers: props.answers,
    dataSource: props.dataSource,
  });

  return (
    <div
      className="onboarding-v1__pick-step onboarding-v1__flow-step"
      data-testid="onboarding-v1-step-pick"
    >
      <div className="onboarding-v1__flow-body">
        <div className="onboarding-v1__pick-heading">
          <h1>{messages.onboarding_v1_pick_heading()}</h1>
          <p>{messages.onboarding_v1_pick_description()}</p>
        </div>

        {recommendations.isLoading ? <PickLoadingState /> : null}
        {!recommendations.isLoading && recommendations.isError ? (
          <OnboardingV1RetryState
            className="onboarding-v1__pick-error"
            onRetry={recommendations.retry}
          />
        ) : null}
        {!recommendations.isLoading && !recommendations.isError ? (
          <div
            aria-label={messages.onboarding_v1_pick_group_label()}
            className="onboarding-v1__portfolio-cards"
            role="group"
          >
            {recommendations.cards.map((card) => (
              <RecommendationCard
                card={card}
                key={card.blueprintId}
                onSelect={() => props.onSelect(card.blueprintId)}
                selected={props.selectedBlueprintId === card.blueprintId}
              />
            ))}
          </div>
        ) : null}
      </div>

      <div className="onboarding-v1__flow-actions onboarding-v1__pick-actions">
        {!recommendations.isLoading && !recommendations.isError ? (
          <>
            <Button
              className="onboarding-v1__pick-continue"
              disabled={props.selectedBlueprintId === null}
              onClick={props.onContinue}
              size="lg"
              type="button"
              variant="inverse"
            >
              {messages.onboarding_v1_pick_continue_action()}
            </Button>
            {/* Temporarily hidden while onboarding requires a portfolio choice.
            <Button
              className="onboarding-v1__pick-explore"
              onClick={props.onExplore}
              type="button"
              variant="link"
            >
              {messages.onboarding_v1_pick_explore_action()}
            </Button>
            */}
          </>
        ) : null}
      </div>
    </div>
  );
}
